import type { ColumnDef } from "@tanstack/react-table";
import { BreadcrumbUI } from "components/Breadcrumb/Breadcrumb";
import {
  BreadcrumbContextProvider,
  useBreadcrumb,
} from "components/Breadcrumb/context";
import { ContentBoxUI } from "components/ContentBox/ContentBox";
import { DismissibleAlert } from "components/DismissibleAlert/DismissibleAlert";
import { FormGroupUI } from "components/FormGroup/FormGroup";
import { FormGroupLabelUI } from "components/FormGroupLabel/FormGroupLabel";
import { LoadingUI } from "components/Loading/Loading";
import { PageTitle } from "components/PageTitle/PageTitle";
import { Badge } from "components/ui/Badge/Badge";
import { Button } from "components/ui/Button/Button";
import { CreatableMultiSelect } from "components/ui/CreatableMultiSelect/CreatableMultiSelect";
import { DateTimeInput } from "components/ui/DateTimeInput/DateTimeInput";
import { FieldWrapper } from "components/ui/FieldWrapper/FieldWrapper";
import { FromToRange } from "components/ui/FromToRange/FromToRange";
import { Input } from "components/ui/Input/Input";
import { Link } from "components/ui/Link/Link";
import { Loading } from "components/ui/Loading/Loading";
import { RadioGroup } from "components/ui/RadioGroup/RadioGroup";
import { SegmentedControl } from "components/ui/SegmentedControl/SegmentedControl";
import { Select } from "components/ui/Select/Select";
import { DataTable } from "components/ui/Table/DataTable";
import { Tabs } from "components/ui/Tabs/Tabs";
import { Textarea } from "components/ui/Textarea/Textarea";
import { Toggle } from "components/ui/Toggle/Toggle";
import { Megaphone, Send } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { VariantMatrix } from "routes/SnippetsPage/components/VariantMatrix";
import type {
  SnippetDefinition,
  SnippetMetadata,
} from "routes/SnippetsPage/registry";
import { snippetMetadata } from "routes/SnippetsPage/registry";

// ---- axes ----

const buttonVariants = [
  "default",
  "secondary",
  "destructive",
  "ghost",
  "outline",
  "link",
  "warning",
  "success",
  "info",
  "accent",
] as const;
const buttonSizes = ["default", "xs", "sm", "lg", "icon"] as const;

const badgeColors = [
  "default",
  "primary",
  "secondary",
  "accent",
  "ghost",
  "info",
  "success",
  "warning",
  "error",
] as const;
const badgeSizes = ["xs", "sm", "md", "lg"] as const;

const inputSizes = ["sm", "md", "lg"] as const;
const inputTones = ["default", "invalid"] as const;

const toggleSizes = ["xs", "sm", "md", "lg"] as const;
const toggleColors = [
  "default",
  "primary",
  "secondary",
  "accent",
  "info",
  "success",
  "warning",
  "error",
] as const;

const rangeSizes = ["xs", "sm", "md", "lg"] as const;
const rangeColors = [
  "default",
  "primary",
  "secondary",
  "info",
  "accent",
  "success",
  "warning",
  "error",
] as const;

const selectWidths = ["narrow", "full"] as const;

// ---- render functions ----

const renderButton = (): ReactNode => (
  <div className="space-y-6">
    <div>
      <p className="text-xs text-base-content/60 mb-2">variant × size</p>
      <VariantMatrix
        rowAxis={{ name: "variant", values: buttonVariants }}
        colAxis={{ name: "size", values: buttonSizes }}
        render={(variant, size) => (
          <Button variant={variant} size={size}>
            Button
          </Button>
        )}
      />
    </div>
    <div>
      <p className="text-xs text-base-content/60 mb-2">isOutline</p>
      <div className="flex gap-2 flex-wrap">
        {buttonVariants.map(variant => (
          <Button key={variant} variant={variant} isOutline>
            {variant}
          </Button>
        ))}
      </div>
    </div>
    <div>
      <p className="text-xs text-base-content/60 mb-2">width</p>
      <div className="flex flex-col gap-2 items-start max-w-sm">
        <Button width="narrow">narrow (w-40)</Button>
        <Button width="full">full (w-full)</Button>
      </div>
    </div>
    <div>
      <p className="text-xs text-base-content/60 mb-2">
        isJoinItem (join コンテナ内で利用)
      </p>
      <div className="join">
        <Button isJoinItem>左</Button>
        <Button isJoinItem variant="secondary">
          中
        </Button>
        <Button isJoinItem variant="accent">
          右
        </Button>
      </div>
    </div>
    <div>
      <p className="text-xs text-base-content/60 mb-2">
        states (normal / hover / active / focus / disabled)
      </p>
      <p className="text-[10px] text-base-content/50 mb-2">
        hover / active は CSS filter による模擬表示。normal
        は実際にマウスオーバー / クリックで生の挙動を確認できます。focus は Tab
        キーで遷移するか、 ボタンをクリック後に確認してください。
      </p>
      <div className="overflow-x-auto">
        <table className="table table-xs">
          <thead>
            <tr>
              <th className="text-base-content/50 text-xs font-normal">
                variant ＼ state
              </th>
              <th className="text-base-content/70 text-xs font-medium">
                normal
              </th>
              <th className="text-base-content/70 text-xs font-medium">
                hover
              </th>
              <th className="text-base-content/70 text-xs font-medium">
                active
              </th>
              <th className="text-base-content/70 text-xs font-medium">
                focus
              </th>
              <th className="text-base-content/70 text-xs font-medium">
                disabled
              </th>
            </tr>
          </thead>
          <tbody>
            {buttonVariants.map(variant => (
              <tr key={variant}>
                <td className="text-base-content/70 text-xs font-medium whitespace-nowrap">
                  {variant}
                </td>
                <td>
                  <Button variant={variant}>Button</Button>
                </td>
                <td>
                  <div style={{ filter: "brightness(0.9)" }}>
                    <Button variant={variant}>Button</Button>
                  </div>
                </td>
                <td>
                  <div style={{ filter: "brightness(0.75)" }}>
                    <Button variant={variant}>Button</Button>
                  </div>
                </td>
                <td>
                  <Button
                    variant={variant}
                    style={{
                      outline: "2px solid hsl(var(--ring))",
                      outlineOffset: "2px",
                    }}
                  >
                    Button
                  </Button>
                </td>
                <td>
                  <Button variant={variant} disabled>
                    Button
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const renderBadge = (): ReactNode => (
  <div className="space-y-6">
    <div>
      <p className="text-xs text-base-content/60 mb-2">
        color × size (xs / sm / md / lg)
      </p>
      <VariantMatrix
        rowAxis={{ name: "color", values: badgeColors }}
        colAxis={{ name: "size", values: badgeSizes }}
        render={(color, size) => (
          <Badge color={color} size={size}>
            badge
          </Badge>
        )}
      />
    </div>
    <div>
      <p className="text-xs text-base-content/60 mb-2">
        icon バリアント (アイコン + テキスト)
      </p>
      <div className="flex gap-2 flex-wrap items-center">
        {badgeSizes.map(size => (
          <Badge key={size} color="info" size={size} icon={Send}>
            個別 ({size})
          </Badge>
        ))}
        {badgeSizes.map(size => (
          <Badge key={`m-${size}`} tone="stage-1" size={size} icon={Megaphone}>
            一斉 ({size})
          </Badge>
        ))}
      </div>
    </div>
    <div>
      <p className="text-xs text-base-content/60 mb-2">
        tone escape hatch (BadgeTone 直接指定)
      </p>
      <div className="flex gap-2 flex-wrap">
        {(
          [
            "stage-1",
            "stage-2",
            "stage-3",
            "stage-4",
            "stage-final",
            "critical-error",
            "critical-success",
            "tag-neutral",
          ] as const
        ).map(t => (
          <Badge key={t} tone={t}>
            {t}
          </Badge>
        ))}
      </div>
    </div>
    <div>
      <p className="text-xs text-base-content/60 mb-2">isOutline</p>
      <div className="flex gap-2 flex-wrap">
        {badgeColors.map(color => (
          <Badge key={color} color={color} isOutline>
            {color}
          </Badge>
        ))}
      </div>
    </div>
  </div>
);

const renderInput = (): ReactNode => (
  <VariantMatrix
    rowAxis={{ name: "tone", values: inputTones }}
    colAxis={{ name: "size", values: inputSizes }}
    render={(tone, size) => (
      <Input size={size} tone={tone} placeholder={`${size} / ${tone}`} />
    )}
  />
);

const renderTextarea = (): ReactNode => (
  <VariantMatrix
    rowAxis={{ name: "tone", values: inputTones }}
    colAxis={{ name: "size", values: inputSizes }}
    render={(tone, size) => (
      <Textarea
        size={size}
        tone={tone}
        placeholder={`${size} / ${tone}`}
        rows={2}
      />
    )}
  />
);

const renderToggle = (): ReactNode => (
  <div className="space-y-6">
    <div>
      <p className="text-xs text-base-content/60 mb-2">color × size</p>
      <VariantMatrix
        rowAxis={{ name: "color", values: toggleColors }}
        colAxis={{ name: "size", values: toggleSizes }}
        render={(color, size) => (
          <Toggle color={color} size={size} defaultChecked />
        )}
      />
    </div>
    <div>
      <p className="text-xs text-base-content/60 mb-2">with label</p>
      <Toggle label="通知を受け取る" defaultChecked />
    </div>
  </div>
);

const SelectDemo = ({
  width,
  isDisabled,
}: {
  width: "narrow" | "full";
  isDisabled?: boolean;
}): JSX.Element => {
  const [value, setValue] = useState<string | undefined>("a");
  return (
    <Select
      options={[
        { value: "a", label: "選択肢 A" },
        { value: "b", label: "選択肢 B" },
        { value: "c", label: "選択肢 C" },
      ]}
      value={value}
      onChange={setValue}
      width={width}
      isDisabled={isDisabled}
    />
  );
};

const renderSelect = (): ReactNode => (
  <div className="space-y-4">
    {selectWidths.map(width => (
      <div key={width}>
        <p className="text-xs text-base-content/60 mb-1">width = {width}</p>
        <SelectDemo width={width} />
      </div>
    ))}
    <div>
      <p className="text-xs text-base-content/60 mb-1">isDisabled</p>
      <SelectDemo width="narrow" isDisabled />
    </div>
  </div>
);

const RadioGroupDemo = ({
  orientation,
}: {
  orientation: "horizontal" | "vertical";
}): JSX.Element => {
  const [value, setValue] = useState("a");
  return (
    <RadioGroup
      name={`radio-${orientation}`}
      orientation={orientation}
      value={value}
      onChange={setValue}
      options={[
        { value: "a", label: "A" },
        { value: "b", label: "B" },
        { value: "c", label: "C" },
      ]}
    />
  );
};

const renderRadioGroup = (): ReactNode => (
  <div className="space-y-4">
    <div>
      <p className="text-xs text-base-content/60 mb-1">horizontal</p>
      <RadioGroupDemo orientation="horizontal" />
    </div>
    <div>
      <p className="text-xs text-base-content/60 mb-1">vertical</p>
      <RadioGroupDemo orientation="vertical" />
    </div>
  </div>
);

const renderFieldWrapper = (): ReactNode => (
  <div className="space-y-4 max-w-md">
    <FieldWrapper label="ラベルのみ">
      <Input placeholder="…" />
    </FieldWrapper>
    <FieldWrapper label="必須" required>
      <Input placeholder="…" />
    </FieldWrapper>
    <FieldWrapper label="ヒント付き" hint="半角英数字で入力してください">
      <Input placeholder="…" />
    </FieldWrapper>
    <FieldWrapper label="エラー付き" error="必須項目です">
      <Input tone="invalid" placeholder="…" />
    </FieldWrapper>
  </div>
);

const renderDateTimeInput = (): ReactNode => (
  <div className="max-w-xs">
    <DateTimeInput />
  </div>
);

const FromToRangeDemo = ({
  size,
  color,
}: {
  size: (typeof rangeSizes)[number];
  color: (typeof rangeColors)[number];
}): JSX.Element => {
  const [from, setFrom] = useState(20);
  const [to, setTo] = useState(80);
  return (
    <FromToRange
      min={0}
      max={100}
      step={5}
      fromValue={from}
      toValue={to}
      onFromChange={setFrom}
      onToChange={setTo}
      unitName="万"
      size={size}
      color={color}
    />
  );
};

const renderFromToRange = (): ReactNode => (
  <div className="space-y-6">
    <div>
      <p className="text-xs text-base-content/60 mb-2">size バリエーション</p>
      <div className="grid gap-4 max-w-md">
        {rangeSizes.map(size => (
          <div key={size}>
            <p className="text-[10px] text-base-content/50 mb-1">{size}</p>
            <FromToRangeDemo size={size} color="primary" />
          </div>
        ))}
      </div>
    </div>
    <div>
      <p className="text-xs text-base-content/60 mb-2">color バリエーション</p>
      <div className="grid gap-4 max-w-md">
        {rangeColors.map(color => (
          <div key={color}>
            <p className="text-[10px] text-base-content/50 mb-1">{color}</p>
            <FromToRangeDemo size="md" color={color} />
          </div>
        ))}
      </div>
    </div>
  </div>
);

const CreatableDemo = ({
  width,
}: {
  width: "narrow" | "full";
}): JSX.Element => {
  const [tags, setTags] = useState<string[]>(["React", "TypeScript"]);
  return (
    <CreatableMultiSelect
      value={tags}
      onChange={setTags}
      width={width}
      placeholder="タグを追加"
    />
  );
};

const renderCreatableMultiSelect = (): ReactNode => (
  <div className="space-y-4">
    {selectWidths.map(width => (
      <div key={width}>
        <p className="text-xs text-base-content/60 mb-1">width = {width}</p>
        <CreatableDemo width={width} />
      </div>
    ))}
  </div>
);

const renderLink = (): ReactNode => (
  <div className="space-y-4">
    <div>
      <p className="text-xs text-base-content/60 mb-2">color バリエーション</p>
      <div className="flex gap-4 flex-wrap items-center">
        <Link to="/snippets">default (violet-600)</Link>
        <Link to="/snippets" color="primary">
          primary (navy)
        </Link>
        <Link to="/snippets" color="muted">
          muted (base-content/70)
        </Link>
      </div>
    </div>
    <div>
      <p className="text-xs text-base-content/60 mb-2">
        外部リンク (href, 新タブ)
      </p>
      <Link href="https://strixsearch.com" newTab>
        strixsearch.com
      </Link>
    </div>
    <div>
      <p className="text-xs text-base-content/60 mb-2">文中での利用イメージ</p>
      <p className="text-sm">
        タレント詳細は
        <Link to="/crm/talents/sample"> こちらのリンク </Link>
        から確認できます。
      </p>
    </div>
  </div>
);

const renderPageTitle = (): ReactNode => (
  <PageTitle>サンプルタイトル</PageTitle>
);

const renderContentBox = (): ReactNode => (
  <ContentBoxUI title="カードタイトル">
    <p className="text-sm">ContentBoxUI の子要素サンプル</p>
  </ContentBoxUI>
);

const renderFormGroup = (): ReactNode => (
  <FormGroupUI>
    <FormGroupLabelUI>項目名</FormGroupLabelUI>
    <Input placeholder="値" />
  </FormGroupUI>
);

const renderFormGroupLabel = (): ReactNode => (
  <FormGroupLabelUI>項目名</FormGroupLabelUI>
);

const renderDismissibleAlert = (): ReactNode => (
  <div className="space-y-2">
    {(["info", "success", "warning", "error"] as const).map(variant => (
      <DismissibleAlert
        key={variant}
        variant={variant}
        onDismiss={() => undefined}
      >
        variant = {variant}
      </DismissibleAlert>
    ))}
  </div>
);

const renderLoadingLeaf = (): ReactNode => (
  <div className="flex items-end gap-6 flex-wrap">
    {(["xs", "sm", "md", "lg", "xl"] as const).map(size => (
      <div key={size} className="text-center">
        <Loading size={size} />
        <p className="text-[10px] text-base-content/60 mt-1">size={size}</p>
      </div>
    ))}
  </div>
);

const renderLoading = (): ReactNode => (
  <div className="flex items-center gap-6 flex-wrap">
    {(
      [
        "Audio",
        "BallTriangle",
        "Bars",
        "Circles",
        "Grid",
        "Hearts",
        "Oval",
        "Puff",
        "Rings",
        "TailSpin",
        "ThreeDots",
        "Watch",
        "RevolvingDot",
        "Triangle",
        "Plane",
        "MutatingDots",
        "CradleLoader",
      ] as const
    ).map(type => (
      <div key={type} className="text-center">
        <LoadingUI type={type} width={40} height={40} color="#02072F" />
        <p className="text-[10px] text-base-content/60 mt-1">{type}</p>
      </div>
    ))}
  </div>
);

const BreadcrumbDemoBody = (): JSX.Element => {
  const { setBreadcrumbItems } = useBreadcrumb();
  useEffect(() => {
    setBreadcrumbItems([
      { label: "Home", to: "/dashboard" },
      { label: "設定", to: "/settings/tenant" },
      { label: "詳細" },
    ]);
  }, [setBreadcrumbItems]);
  return <BreadcrumbUI />;
};

const renderBreadcrumb = (): ReactNode => (
  <BreadcrumbContextProvider>
    <BreadcrumbDemoBody />
  </BreadcrumbContextProvider>
);

const TabsDemo = ({ withCount }: { withCount: boolean }): JSX.Element => {
  const [value, setValue] = useState<"all" | "unresponded" | "completed">(
    "unresponded",
  );
  return (
    <Tabs<"all" | "unresponded" | "completed">
      value={value}
      items={[
        { value: "all", label: "全て", count: withCount ? 15 : null },
        { value: "unresponded", label: "未対応", count: withCount ? 10 : null },
        { value: "completed", label: "完了", count: withCount ? 0 : null },
      ]}
      onChange={setValue}
    />
  );
};

const renderTabs = (): ReactNode => (
  <div className="space-y-6">
    <div>
      <p className="text-xs text-base-content/60 mb-2">count あり</p>
      <TabsDemo withCount />
    </div>
    <div>
      <p className="text-xs text-base-content/60 mb-2">count なし</p>
      <TabsDemo withCount={false} />
    </div>
  </div>
);

const SegmentedControlDemo = ({
  withIcon,
  withLabel,
}: {
  withIcon: boolean;
  withLabel: boolean;
}): JSX.Element => {
  const [value, setValue] = useState<"all" | "delivery" | "sent_email">("all");
  return (
    <SegmentedControl<"all" | "delivery" | "sent_email">
      value={value}
      label={withLabel ? "ラベル" : undefined}
      items={[
        { value: "all", label: "全て" },
        {
          value: "delivery",
          label: "一斉",
          icon: withIcon ? Megaphone : undefined,
        },
        {
          value: "sent_email",
          label: "個別",
          icon: withIcon ? Send : undefined,
        },
      ]}
      onChange={setValue}
    />
  );
};

const renderSegmentedControl = (): ReactNode => (
  <div className="space-y-6">
    <div>
      <p className="text-xs text-base-content/60 mb-2">label + icon</p>
      <SegmentedControlDemo withIcon withLabel />
    </div>
    <div>
      <p className="text-xs text-base-content/60 mb-2">label のみ</p>
      <SegmentedControlDemo withIcon={false} withLabel />
    </div>
    <div>
      <p className="text-xs text-base-content/60 mb-2">最小</p>
      <SegmentedControlDemo withIcon={false} withLabel={false} />
    </div>
  </div>
);

// ---- DataTable demo data ----

type SampleRow = {
  id: string;
  name: string;
  email: string;
  role: "admin" | "member" | "guest";
  createdAt: string;
};

const sampleRows: SampleRow[] = Array.from({ length: 37 }).map((_, i) => ({
  id: `u${String(i + 1).padStart(3, "0")}`,
  name: [
    "山田 太郎",
    "佐藤 花子",
    "鈴木 一郎",
    "田中 美咲",
    "高橋 健",
    "伊藤 優子",
    "渡辺 翔",
    "中村 葵",
  ][i % 8] as string,
  email: `user${i + 1}@example.com`,
  role: (["admin", "member", "guest"] as const)[i % 3] as SampleRow["role"],
  createdAt: new Date(2026, 0, (i % 28) + 1).toISOString().slice(0, 10),
}));

const sampleColumns: ColumnDef<SampleRow, any>[] = [
  { accessorKey: "id", header: "ID" },
  { accessorKey: "name", header: "氏名" },
  { accessorKey: "email", header: "メール" },
  { accessorKey: "role", header: "ロール" },
  { accessorKey: "createdAt", header: "作成日" },
];

const renderDataTable = (): ReactNode => (
  <div className="space-y-8">
    <div>
      <p className="text-xs text-base-content/60 mb-2">
        default (zebra + stickyHeader + density=compact + sort + pagination、37
        行)
      </p>
      <DataTable
        columns={sampleColumns}
        data={sampleRows}
        sortable
        pagination={{ pageSize: 10 }}
      />
    </div>
    <div>
      <p className="text-xs text-base-content/60 mb-2">
        density=normal / zebra=false / stickyHeader=false
        （デフォルトを打ち消した例）
      </p>
      <DataTable
        columns={sampleColumns}
        data={sampleRows.slice(0, 8)}
        sortable
        density="normal"
        zebra={false}
        stickyHeader={false}
      />
    </div>
    <div>
      <p className="text-xs text-base-content/60 mb-2">bordered + row click</p>
      <DataTable
        columns={sampleColumns}
        data={sampleRows.slice(0, 5)}
        sortable
        bordered
        onRowClick={row => {
          // eslint-disable-next-line no-alert
          alert(`clicked: ${row.name}`);
        }}
      />
    </div>
    <div>
      <p className="text-xs text-base-content/60 mb-2">isLoading (skeleton)</p>
      <DataTable columns={sampleColumns} data={[]} isLoading />
    </div>
    <div>
      <p className="text-xs text-base-content/60 mb-2">empty state</p>
      <DataTable
        columns={sampleColumns}
        data={[]}
        emptyState="該当するユーザーがいません"
      />
    </div>
  </div>
);

const rendererMap: Record<string, () => ReactNode> = {
  "LEAF-0001": renderButton,
  "LEAF-0002": renderBadge,
  "LEAF-0003": renderInput,
  "LEAF-0004": renderTextarea,
  "LEAF-0005": renderToggle,
  "LEAF-0006": renderSelect,
  "LEAF-0007": renderRadioGroup,
  "LEAF-0008": renderFieldWrapper,
  "LEAF-0009": renderDateTimeInput,
  "LEAF-0010": renderFromToRange,
  "LEAF-0011": renderCreatableMultiSelect,
  "LEAF-0012": renderLink,
  "LEAF-0013": renderDataTable,
  "COMP-0001": renderPageTitle,
  "COMP-0002": renderContentBox,
  "COMP-0003": renderFormGroup,
  "COMP-0004": renderFormGroupLabel,
  "COMP-0005": renderDismissibleAlert,
  "COMP-0006": renderLoading,
  "LEAF-0014": renderLoadingLeaf,
  "LEAF-0015": renderTabs,
  "LEAF-0016": renderSegmentedControl,
  "COMP-0007": renderBreadcrumb,
};

/**
 * snippetMetadata に renderer をマージして完成品の SnippetDefinition 配列を返す。
 * @return {SnippetDefinition[]} 完成版スニペット定義
 */
export const buildSnippetRegistry = (): SnippetDefinition[] => {
  return snippetMetadata.map((meta: SnippetMetadata) => {
    const render = rendererMap[meta.id];
    return {
      ...meta,
      render:
        render ??
        (() => (
          <p className="text-xs text-base-content/50">
            renderer 未実装（rendererMap に追加してください）
          </p>
        )),
    };
  });
};
